import { FC } from "react";
import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Link } from "react-router-dom";

import { Button, Image } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { baseContainerMaxWidthStyles } from "containers/BaseContainer";
import { useAppConfigs } from "services/appConfigs";
import theme from "theme";
import { Theme } from "theme";

import HeaderBox from "./HeaderBox";

const royaltyPayorsImages = [
  "rstones.png",
  "latimes.svg",
  "forbes.png",
  "billboard.png",
  "fast.png",
  "thewall.png",
] as const;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerContainer: {
      ...baseContainerMaxWidthStyles,
      [theme.breakpoints.only("xs")]: {
        textAlign: "center",
      },
    },
    headerTitle: {
      paddingBottom: theme.spacing(2),
    },
    headerSubtitle: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.25,
      textTransform: "none",
      maxWidth: "600px",
      [theme.breakpoints.down("md")]: {
        maxWidth: "unset",
      },
    },
    headerImagesContainer: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(3),
      opacity: "0.6",
      textAlign: "center",
    },
    headerImage: {
      maxHeight: "15px",
      padding: `0 ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`,
    },
    headerButton: {
      width: "250px",
    },
  }),
);

export type HeaderButtonContentTypes = {
  label: string;
  path: string;
};

export interface OrderBookPagesHeaderProps {
  h1Content: { default: string; xsDown?: string };
  subtitle1Content: string;
  headerButtonContent: HeaderButtonContentTypes;
}

const OrderBookPagesHeader: FC<OrderBookPagesHeaderProps> = ({
  h1Content,
  subtitle1Content,
  headerButtonContent,
}) => {
  const classes = useStyles();
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const appConfigsHeaderStats = useAppConfigs([
    "COMPLETED_TRANSACTIONS_PROCEEDS",
    "COMPLETED_TRANSACTIONS_AVERAGE_YIELD",
  ]);

  const transactions = {
    value:
      appConfigsHeaderStats.data?.COMPLETED_TRANSACTIONS_PROCEEDS ||
      EMPTY_VALUE_PLACEHOLDER,
    title: "Transactions completed",
  };
  const average = {
    value:
      appConfigsHeaderStats.data?.COMPLETED_TRANSACTIONS_AVERAGE_YIELD ||
      EMPTY_VALUE_PLACEHOLDER,
    title: "Average yield",
  };

  const boxValues = [transactions, average];

  return (
    <Box
      style={{
        backgroundColor: theme.palette.background.default,
        paddingTop: theme.spacing(5),
      }}
    >
      <Box pt={isSmDown ? 4 : 12} className={classes.headerContainer}>
        <Grid container spacing={4}>
          <Grid
            container
            item
            md={6}
            justify={isSmDown ? "center" : "flex-start"}
          >
            <Box textAlign={isSmDown ? "center" : "left"}>
              <Typography variant="h1" className={classes.headerTitle}>
                {isXsDown && h1Content.xsDown
                  ? h1Content.xsDown
                  : h1Content.default}
              </Typography>
              <Typography
                variant="subtitle1"
                className={classes.headerSubtitle}
              >
                {subtitle1Content}
              </Typography>
            </Box>
            <Box className={classes.headerButton} mt={3}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                component={Link}
                to={headerButtonContent.path}
              >
                {headerButtonContent.label}
              </Button>
            </Box>
          </Grid>
          <Grid container item md={6}>
            <Grid
              container
              item
              spacing={2}
              justify="center"
              alignItems={isMdDown ? "flex-end" : "center"}
            >
              {boxValues.map(data => (
                <HeaderBox
                  key={data.title}
                  title={data.title}
                  value={data.value}
                />
              ))}
            </Grid>
            <Grid container>
              {royaltyPayorsImages.map((image, index) => (
                <Grid
                  item
                  key={image + index}
                  xs={4}
                  sm={2}
                  className={classes.headerImagesContainer}
                >
                  <Image name={image} className={classes.headerImage} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OrderBookPagesHeader;
